import React, { useEffect, useMemo } from 'react'
import {
	generatePath,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom'
import type {
	DocumentData,
	File as ServerFile,
	LoanDocument,
} from '@matech/thebigpos-sdk'
import { useVenti } from 'venti'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useIntl } from 'react-intl'
import CloseIcon from '@mui/icons-material/Close'
import { getTheme } from '../../../config'
import { getErrorMessage } from '../../../services/helper'
import { useAlert } from '../../../hooks'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import MediaLibrary from '../../../components/MediaLibrary/MediaLibrary'
import {
	getLoanDocumentFolders,
	sortLoanDocuments,
} from './utils/helpers'
import useUser from '../../../hooks/useUser'
import useSearch from '../../../hooks/useSearch'
import usePagination from '../../../hooks/usePagination'
import {
	downloadLoanDocument,
	getNameExtensionFromFileName,
} from '../../../services/helpers'
import {
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
} from '../../../components/MediaLibrary/utils/constants'
import ModalDownloadDocuments from '../../../components/modals/ModalDownloadDocuments'
import { eventTypes } from '../../../services/constants'
import { useMixpanel } from '../../../hooks/useMixpanel'
import {
	BaseMediaLibraryProps,
	MediaFileProps,
} from '../../../components/MediaLibrary/types'
import { useLoanContext } from '../../../contexts/LoanContext'
import AlertBox from '../../../components/AlertBox'
import IconButton from '../../../components/Button/IconButton'
import { navigationLinking } from '../../../services/navigation'
import ModalSendDocuments from '../../../components/modals/ModalSendDocuments'

const theme = getTheme()

type DocumentDataWithProcessing = DocumentData & {
	processing?: boolean
}

const LoanDocumentsContent = () => {
	const { alert } = useAlert()
	const ventiState = useVenti()
	const { documentId } = useParams()
	const { loanId } = useLoanContext()
	const preloadedDocs = ventiState.get(theme.storageKeys.loanDocs)
	const { isRealtor, isSettlementAgent, isLoanAdmin } = useUser()
	const queryClient = useQueryClient()
	const [searchParams, setSearchParams] = useSearchParams()
	const { formatMessage } = useIntl()
	const mixpanel = useMixpanel()
	const navigate = useNavigate()

	const [
		isLockedUploadNoticeVisible,
		setIsLockedUploadNoticeVisible,
	] = React.useState(false)

	const downloadFileIds: string[] = JSON.parse(
		searchParams.get('downloadFiles') ?? '[]'
	)

	const getDefaultDocumentType = useMemo(() => {
		if (isRealtor) return 'Partner Portal Document Vault'
		if (isSettlementAgent) return 'Settlement Agent Vault'
		return 'Borrower Portal Document Vault'
	}, [isRealtor, isSettlementAgent])

	const [documentsToSend, setDocumentsToSend] = React.useState<
		MediaFileProps[]
	>([])
	const {
		data: taskDocumentsByLoan,
		error: taskDocumentsByLoanError,
		refetch: refetchTaskDocumentsByLoan,
		isFetching: fetchingTaskDocumentsByLoan,
	} = useQuery({
		queryKey: ['loanDocuments', loanId],
		queryFn: () => TheBigPOSApi.getTaskDocumentsByLoan(loanId ?? ''),
		enabled: !!loanId,
		select: (data) => data.data,
	})

	const loanDocuments: MediaFileProps[] = React.useMemo(() => {
		return ((taskDocumentsByLoan || preloadedDocs) ?? []).map(
			(doc: DocumentDataWithProcessing): MediaFileProps => {
				const { extension } = getNameExtensionFromFileName(
					doc.documentName ?? ''
				)
				return {
					id: doc.documentID,
					fileName: doc.documentName ?? '',
					extension: doc.extension ?? extension,
					name: doc.documentName ?? '',
					parentFolder:
						doc.documentBucketTitle ?? getDefaultDocumentType,
					createdAt: doc.createdAt,
					losId: doc.documentID,
					url: undefined,
					processing: doc.processing ?? false,
				}
			}
		)
	}, [taskDocumentsByLoan, preloadedDocs, getDefaultDocumentType])

	const documentFolders = getLoanDocumentFolders(
		taskDocumentsByLoan || preloadedDocs,
		getDefaultDocumentType
	)

	const preSelectedFiles = useMemo(() => {
		const document = loanDocuments.find(
			(doc) => doc.id === documentId
		)
		return document ? [document] : []
	}, [documentId, loanDocuments])

	const uploadDocumentMutation = useMutation({
		mutationFn: (document: {
			name: string
			file: ServerFile
			bucket: string
		}) => TheBigPOSApi.createLoanDocument(loanId ?? '', document),
		onSuccess: (data) => {
			alert(
				formatMessage(
					{ id: 'upload_media.alerts.upload_success' },
					{
						name: data.data.name,
					}
				),
				{ severity: 'success' }
			)

			queryClient.setQueryData(
				['loanDocuments', loanId],
				(prevData: AxiosResponse<DocumentData[]>) => {
					if (!prevData) return [data.data]
					const { extension } = getNameExtensionFromFileName(
						data.data.name
					)

					if (!data.data.losDocumentID) {
						setIsLockedUploadNoticeVisible(true)
					}
					return {
						...prevData,
						data: [
							...prevData.data,
							{
								documentID: data.data.losDocumentID ?? data.data.id,
								documentName: data.data.name,
								extension,
								documentBucketTitle: data.data.initialBucket,
								createdAt: data.data.createdAt,
								processing: !data.data.losDocumentID,
							},
						],
					}
				}
			)
		},
		onError: (e) => {
			alert(getErrorMessage(e), { severity: 'error' })
		},
	})

	const search = useSearch()
	const pagination = usePagination({
		defaultSortBy: DEFAULT_SORT_BY,
		defaultSortDirection: DEFAULT_SORT_DIRECTION,
	})

	const handleDocumentUpload = async (files: File[]) => {
		const mutations: Promise<AxiosResponse<LoanDocument>>[] = []
		files.forEach((file: File) => {
			const document = {
				name: file.name,
				file: file as unknown as ServerFile,
				bucket: getDefaultDocumentType,
			}
			mutations.push(uploadDocumentMutation.mutateAsync(document))
		})
		await Promise.all(mutations)
	}

	const handleSearchChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newSearchText = e.target.value
		search?.handleSearchChange?.(e)

		if (newSearchText.split(',').length > 1) {
			const fileIds = newSearchText
				.split(',')
				.map((id) => id.trim())
				.filter((id) => id)
			setSearchParams({ selectedFiles: JSON.stringify(fileIds) })
		}
	}

	const handleClearSearchClick = () => {
		search?.handleClearSearchClick?.()
	}

	const handleSort = (
		sortBy: keyof MediaFileProps,
		direction: 'asc' | 'desc'
	) => {
		pagination?.handleSort?.({ sortField: sortBy }, direction)
	}

	const sortedFilteredData = React.useMemo(() => {
		const searchText = search?.searchText?.toLowerCase()
		const sortedLoanDocuments = sortLoanDocuments(
			loanDocuments,
			pagination?.sortBy as keyof MediaFileProps,
			pagination?.sortDirection
		)
		return sortedLoanDocuments.filter((media) =>
			media.name.toLowerCase().includes(searchText.toLowerCase())
		)
	}, [
		loanDocuments,
		pagination?.sortBy,
		pagination?.sortDirection,
		search?.searchText,
	])

	const downloadFiles = useMemo(() => {
		return downloadFileIds
			.map((id: string) =>
				sortedFilteredData.find(
					(file) => file.id === id && !file.processing
				)
			)
			.filter((file) => !!file) // Boolean not accepted by TS
	}, [downloadFileIds, sortedFilteredData])

	const handleOpenDownloadModal = (files?: MediaFileProps[]) => {
		if (!files || files.length === 0) {
			return
		}
		setSearchParams({
			downloadFiles: JSON.stringify(files.map((file) => file.id)),
		})
	}

	const handleDownloadFiles = () => {
		setSearchParams({})
	}

	const handleCloseDownloadModal = () => {
		setSearchParams({})
	}

	const initiateDownload = (documentId: string) => {
		mixpanel.trackEvent(eventTypes.DOCUMENT_TEMPLATE_DOWNLOADED, {
			documentId,
		})
		if (!loanId) return
		downloadLoanDocument(loanId, documentId)
	}

	const handleDownloadPreviewClick = (file?: MediaFileProps) => {
		if (!file || !loanId) return

		const { id } = file

		initiateDownload(id)
	}
	const handleOpenSendDocumentsModal = (files?: MediaFileProps[]) => {
		if (!files || files.length === 0) {
			return
		}
		setDocumentsToSend(files.filter((file) => !file.processing))
	}

	const handleSendPreviewClick = (file?: MediaFileProps) => {
		if (!file || file.processing) return
		setDocumentsToSend([file])
	}

	const handleCloseSendDocumentsModal = () => {
		setDocumentsToSend([])
	}

	const handleRemoveDocument = async (documentId: string) => {
		setDocumentsToSend((prev) =>
			prev.filter((doc) => doc.id !== documentId)
		)
	}

	const handleCloseLockedUploadNotice = () => {
		setIsLockedUploadNoticeVisible(false)
	}

	const handleSelect = (
		files: MediaFileProps[],
		bulkSelect: boolean
	) => {
		if (files.length !== 1 || bulkSelect) return

		const [selectedFile] = files

		navigate(
			generatePath(`/${navigationLinking.Document}`, {
				loanId,
				documentId: selectedFile.id,
			})
		)
	}

	const handleCloseMediaPreview = () => {
		navigate(
			generatePath(`/${navigationLinking.Documents}`, {
				loanId,
			})
		)
	}

	useEffect(() => {
		if (!taskDocumentsByLoanError) return

		alert(getErrorMessage(taskDocumentsByLoanError), {
			severity: 'error',
		})
	}, [taskDocumentsByLoanError, alert])

	const selectActions = [
		isLoanAdmin && {
			text: formatMessage({ id: 'global.send' }),
			onClick: handleSendPreviewClick,
			variant: 'outlined',
		},
		{
			text: formatMessage({ id: 'global.download' }),
			onClick: handleDownloadPreviewClick,
			variant: 'contained',
		},
	].filter(Boolean) as BaseMediaLibraryProps['selectActions']

	const bulkSelectActions = [
		isLoanAdmin && {
			text: formatMessage({ id: 'global.send' }),
			onClick: handleOpenSendDocumentsModal,
			variant: 'outlined',
		},
		{
			text: formatMessage({ id: 'global.download' }),
			onClick: handleOpenDownloadModal,
			variant: 'contained',
		},
	].filter(Boolean) as BaseMediaLibraryProps['bulkSelectActions']

	return (
		<>
			<ModalSendDocuments
				documents={documentsToSend}
				open={documentsToSend.length > 0}
				onCancel={handleCloseSendDocumentsModal}
				onRemoveDocument={handleRemoveDocument}
			/>
			<ModalDownloadDocuments
				documents={downloadFiles}
				onSubmit={handleDownloadFiles}
				open={downloadFiles.length > 0}
				onClose={handleCloseDownloadModal}
			/>
			<div className="relative">
				{isLockedUploadNoticeVisible && (
					<div className="absolute w-full left-0 flex items-center justify-center z-100">
						<AlertBox severity="info">
							<div className="flex gap-2">
								<p>
									{formatMessage({
										id: 'loan_documents.locked_upload_notice',
									})}
								</p>
								<IconButton
									className="absolute top-0 right-0"
									sx={{ p: 0 }}
									onClick={handleCloseLockedUploadNotice}
									tooltipTitle={formatMessage({ id: 'global.close' })}
								>
									<CloseIcon />
								</IconButton>
							</div>
						</AlertBox>
					</div>
				)}

				<MediaLibrary
					data={sortedFilteredData}
					folders={documentFolders}
					title={formatMessage({ id: 'loan_documents.heading' })}
					bulkSelectActions={bulkSelectActions}
					selectActions={selectActions}
					loading={
						fetchingTaskDocumentsByLoan ||
						uploadDocumentMutation.isPending
					}
					search={{
						searchText: search?.searchText,
						handleSearchChange,
						handleClearSearchClick,
					}}
					pagination={{
						...pagination,
						handleSort,
					}}
					onUpload={handleDocumentUpload}
					onSelect={handleSelect}
					onRefreshClick={refetchTaskDocumentsByLoan}
					onCloseMediaPreview={handleCloseMediaPreview}
					preSelectedFiles={preSelectedFiles}
				/>
			</div>
		</>
	)
}

export default LoanDocumentsContent
